import { useQuery } from '@tanstack/react-query';
import { publicGet } from '@/lib/api/publicClient';
import { publicConfirm } from '@/lib/api/endpoints';

/**
 * Click-to-Confirm landing payload, mirroring
 * `ConfirmLandingPayload` exported by
 * `apps/operations/src/modules/reviews/click-to-confirm/interfaces/confirm.interfaces.ts`.
 *
 * Intentionally duplicated here (rather than imported from the backend
 * package) because the frontend has no shared-types package with operations.
 */
export type ConfirmStatus = 'PENDING' | 'CONFIRMED' | 'RESCHEDULE_REQUESTED' | 'CANCELLED';

export interface ConfirmLandingPayload {
  appointmentId: string;
  appointment: {
    /** ISO datetime — backend serializes Date as string. */
    startAt: string;
    endAt: string;
    timezone: string;
    timezoneLabel: string;
    serviceLabel?: string;
    technicianName?: string;
    technicianNames: string[];
    address: string;
    arrivalWindowLabel?: string;
    scheduleChanged?: boolean;
  };
  managementAvailable: boolean;
  reschedulePolicy: {
    mode: 'DISABLED' | 'REQUEST' | 'SELF_SERVICE';
    cutoffHours: number;
    /** Server-authored: the appointment is too close to reschedule online. */
    withinCutoff: boolean;
    maxSelfServiceReschedules: number;
  };
  branding: {
    tenantName: string;
    logoUrl?: string;
    primaryColor?: string;
  };
  status: ConfirmStatus;
  isStale: boolean;
}

/**
 * Resolves a Click-to-Confirm token to its landing payload.
 * Errors (invalid / expired token) are surfaced via React Query's
 * `isError` / `error` so the route component can render the
 * `TokenExpiredState` fallback.
 */
export function useConfirmLanding(token: string) {
  return useQuery<ConfirmLandingPayload>({
    queryKey: ['public', 'confirm', 'landing', token],
    queryFn: () => publicGet<ConfirmLandingPayload>(publicConfirm.landing(token)),
    retry: false,
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}
